import { sendUpdate } from "@churchofjesuschrist/eden-appearance";
// Define the modes we will use
const modeStyles = {};
modeStyles.light = {};
modeStyles.dark = {};

// For tracking the names used for modeStyles. This is used to ensure that no duplicate names are used. If a duplicate is found, a warning will be logged.
const modeCssVariableRef = {};

/**
 * Function to standardize the setting of style constants
 *  What do we get from this centralized function?
 *  1 - `--` prefix can be added without it needing to be defined in every definition call
 *  2 - We define the variable reference here so can have the fallback included now, and if we make Appearance required in the future it is a single place we can remove the fallback from.
 *  3 - We can check for duplicate variable names and warn if a duplicate is found
 *  4 - Ensure both light and dark modes have a value set and warn if they don't
 *  5 - Puts values into shared modes object which can be used to generate light/dark variable definition blocks (see getMode toward bottom of file)
 *  6 - light/dark values are set one time, in the same place, then can be used in multiple places (returned CSS var reference, in mode object, etc)
 *    - :root level
 *    - locally scoped Mode level (Mode inside a Mode, like an always dark (inverted) media player)
 *    - fallback default for css var reference
 *  7 - Global values and component values are managed the same way. They only difference is the global ones are exported for easier IDE autocomplete usage. If package level values needed to be shared across multiple components within a package, they could be exported too.
 * @param {string} name - The name of the variable
 * @param {object} options - Options for the style constant
 * @param {string} options.light - the value for the light mode
 * @param {string} options.dark - the value for the dark mode
 * @param {string} options.prefix - the prefix to use for the variable name
 * @param {boolean} options.noDuplicateCheck - if true, will not check for duplicate variable names, used when setting a custom theme which will all be duplicates. Unused for now, but could be used in the future if we add a way to set a custom theme.
 * @returns {string} A generated CSS variable reference
 */
const setStyleConstant = (name, {
  light,
  dark,
  prefix = "--",
  noDuplicateCheck
}) => {
  const combinedName = prefix + name;
  // TODO: if there becomes a way to access the context outside of a component, we could see if they were using a mode and if they are, do not include the fallback since the `:root` definitions would be set
  const cssVarReferenceWithDefault = `var(${combinedName}, ${light})`; // needs a fallback for when a project is not using Appearance
  // name re-use check, see if the name exists already and if the value is different
  if (modeCssVariableRef[combinedName] !== undefined && modeCssVariableRef[combinedName] !== cssVarReferenceWithDefault && !noDuplicateCheck) {
    console.warn(`Duplicate variable name ${combinedName}.
Old Value: ${modeCssVariableRef[combinedName]}
New Value: ${cssVarReferenceWithDefault}`);
  }
  modeCssVariableRef[combinedName] = cssVarReferenceWithDefault;
  light ? modeStyles.light[combinedName] = light : console.warn(`No \`light\` value set for ${name}`);
  dark ? modeStyles.dark[combinedName] = dark : console.warn(`No \`dark\` value set for ${name}`);
  sendUpdate(combinedName, {
    light,
    dark
  });
  // Return the CSS reference to the variable
  return cssVarReferenceWithDefault;
};
export const setAppearanceConstant = (name, {
  light,
  dark,
  prefix = "project"
}) => {
  if (!["project", "eden"].includes(prefix)) {
    console.warn("setAppearanceConstant only allows a prefix of `project` or `eden`, you provided:", prefix);
    return;
  } else {
    prefix = "--" + prefix + "-";
  }
  return setStyleConstant(name, {
    light,
    dark,
    prefix
  });
};

// Only used within eden-style-constants to set global values
export const setAppearanceConstantGlobal = (name, {
  light,
  dark
}) => setStyleConstant(name, {
  light,
  dark,
  prefix: "--"
});

// Return the raw styles for a given modeStyle
export const getAppearanceConstant = modeStyleResult => {
  // this takes a string like `var(--foo, #f00)` and returns `--foo`
  const extractedName = modeStyleResult.match(/--[\w-]+/)[0];
  if (!modeCssVariableRef[extractedName]) {
    console.warn(`No appearance style found for ${extractedName}`);
  }
  return {
    name: extractedName,
    light: modeStyles.light[extractedName],
    dark: modeStyles.dark[extractedName]
  };
};

/*
This is for CSS to be used locally within a component, not to be used globally. The returned CSS should be inserted into the component's CSS block so that it is scoped to the component.

Because this is locally scoped, it makes potential future "themes" difficult as the values are not at the root level to override, but inside an unknown selector.

Potential solutions:
- Add theme related instructions to the context which could be checked for and used to override here
- Add theme related instructions to a new global which could be checked for and used to override here
*/
export const setAppearanceConstantLocal = (name, {
  light,
  dark,
  prefix = "project"
}) => [({
  theme: {
    edenAppearanceMode: mode
  } = {
    edenAppearanceMode: "light"
  }
}) => {
  if (!dark) console.warn(`No \`dark\` value set for ${name}`);

  // The definition of the CSS variable that will be used in the component for dark mode
  const darkCSS = `--${prefix}-${name} :  ${dark};`;
  let css = "";

  // if mode is `dark` return the darkCSS without condition to set the CSS variable that dark mode will use
  if (mode === "dark") css = darkCSS;

  // if mode is `os` return the darkCSS with condition to only show when the OS is set to dark. Because of the wrapper it needs the `&` to reflect the current selector back in the component it will be included within
  if (mode === "os") {
    css = `@media (prefers-color-scheme: dark) {
          & { ${darkCSS} }
        }`;
  }
  return css;
}, () => {
  if (!light) console.warn(`No \`light\` value set for ${name}`);

  // TODO: When theming comes along could do `light = context.theme.local[name] || light` or something
  // Return the CSS reference to the variable
  return `var(--${prefix}-${name}, ${light})`;
}];